import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmationScreen } from '@pkg/components';
import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { apiFactory } from '@app/client/apiFactory/api';
import { Transfers } from '@app/client/resources';
import config from '@app/config';
import { PathParams } from '@app/routes/types';
import { addNotification } from '@app/store/actions/notification';
import { getIncomingTransfers, setUserPendingIncoming } from '@app/store/actions/transfers';

import SecurityCodeInput from '../../Input/SecurityCodeInput';

import './TransferConfirmationScreen.scss';

const { supportLink, transferCodeDigitCount } = config;

export const TransferConfirmationScreen = () => {
    const { t } = useTranslation(['transfers', 'words', 'common', 'translation']);
    const { userPendingIncoming } = useAppSelector((state) => state.transfers);
    const dispatch = useAppDispatch();
    const [code, setCode] = useState('');
    const [invalid, setInvalid] = useState(false);
    const [id, setId] = useState('');

    const navigate = useNavigate();
    const { [PathParams.Yat]: yat } = useParams();
    const handleCode = (code) => {
        setCode(code);
    };

    useEffect(() => {
        if (userPendingIncoming) {
            setId(userPendingIncoming.id);
        }
    }, [userPendingIncoming]);

    const handleError = useCallback(
        (status, error) => {
            setInvalid(true);
            switch (status.code) {
                case 401:
                    dispatch(
                        addNotification({
                            severity: 'alert',
                            message: error.toString(),
                            title: 'Code error',
                        }),
                    );
                    break;
                case 404:
                case 0: {
                    navigate(config.homeUrl);
                    dispatch(
                        addNotification({
                            severity: 'alert',
                            message: 'The sender has cancelled this transfer',
                            title: 'Transfer Error',
                        }),
                    );
                    break;
                }
            }
        },
        [dispatch, navigate],
    );

    const submitOTP = useCallback(
        (e) => {
            e.preventDefault();
            apiFactory()
                .call(Transfers.acceptReceiverOTP, {
                    id,
                    value: {
                        code,
                    },
                })
                .then((r) => {
                    dispatch(setUserPendingIncoming(null));
                    dispatch(getIncomingTransfers());
                    if (r.ok && r.value) {
                        navigate(config.homeUrl);
                    } else {
                        setInvalid(true);
                    }
                    if (!r.ok && r.status && r.error) {
                        handleError(r.status, r.error);
                    }
                })
                .catch((e) => {
                    if (e) {
                        setInvalid(true);
                        handleError(e.status, e.error);
                    }
                });
        },
        [dispatch, handleError, navigate, code, id],
    );

    return (
        <div className="transfer-confirmation">
            <ConfirmationScreen
                yat={yat as string}
                confirmationText={t('accept_transfer_title', { yat: t('translation:yat') })}
                textVariant="compact"
                variant={'transferConfirmation'}
            >
                <form className="transfer-confirmation__content" onSubmit={(e) => submitOTP(e)}>
                    <p className="transfer-confirmation__subtitle">
                        {t('accept_transfer_subtitle', { yat: t('translation:yat') })}
                    </p>
                    <div className="transfer-confirmation__input">
                        <SecurityCodeInput
                            setCompleteCode={handleCode}
                            isInvalid={invalid}
                            digitCount={transferCodeDigitCount}
                            digitOnly
                        />
                    </div>
                    <DepButton
                        onClick={(e) => submitOTP(e)}
                        disabled={code.length < transferCodeDigitCount}
                        variant="primary"
                        size={'large'}
                        display={DepButtonDisplay.minWidth}
                        tabIndex={transferCodeDigitCount + 1}
                    >
                        {t('words:accept')}
                    </DepButton>

                    {/* eslint-disable react/jsx-no-target-blank */}
                    <a
                        href={supportLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="transfer-confirmation__link"
                    >
                        {t('common:i_need_help')}
                    </a>
                </form>
            </ConfirmationScreen>
        </div>
    );
};
